import { LOG } from '../common/util/log';
import {
  AnyControlInstructionFunction,
  ControlInstructionCallContext,
  ControlInstructionFunctions,
} from './control-instruction-types';

/** Matches action with name as first group and arguments as second group: $1($2) */
const controlInstructionRegex = /([^(]+)\(([^)]*)\)/;

/** Matches all opening and closing regular brackets */
const matchBracketsRegex = /[()]/g;

export class ControlInstructionParser<
  TInstructionFunctions extends ControlInstructionFunctions<unknown, TContext>,
  TContext extends ControlInstructionCallContext = ControlInstructionCallContext
> {
  private readonly calledInstructions: Set<string> = new Set();

  constructor(protected readonly controlInstructionFunctions: TInstructionFunctions) {}

  public logContextLabel: (context: TContext) => string = (context: TContext): string => {
    return `${context}`;
  }

  public parseControlInstructions(instructions: string, context: Omit<TContext, 'instructionName'>): void {
    this.calledInstructions.clear();
    const controlInstructions: string[] = instructions.split(';').filter(Boolean);

    for (const controlInstruction of controlInstructions) {
      this.parseControlInstruction(controlInstruction, context as TContext);
    }
  }

  private parseControlInstruction(controlInstruction: string, context: TContext): void {
    LOG.debug(`[${this.logContextLabel(context)}] PARSE ${controlInstruction}`);

    if ((controlInstruction.match(matchBracketsRegex) || []).length !== 2) {
      LOG.warn('Unexpected amount of brackets in control instruction:', controlInstruction);
    }

    const matches: RegExpExecArray = controlInstructionRegex.exec(controlInstruction);

    /**
     * matches must have three elements, because:
     *  - The first element is always the whole matched action, e.g. 'column(4)'.
     *  - The second element is the first matched group, representing the action name, e.g. 'column'.
     *  - The third element represents the action parameters, e.g. '4'.
     *    When no parameters are passed this element is empty string.
     */
    if (!matches || matches.length !== 3) {
      LOG.error('Invalid  control instruction: ' + controlInstruction);
      return;
    }

    const instructionName: string = matches[1];
    const instructionArgs: string[] = matches.length > 0 ? matches[2].split(',') : [];

    const controlInstructionFunction: AnyControlInstructionFunction<TContext> =
      this.controlInstructionFunctions[instructionName];

    if (!controlInstructionFunction || typeof controlInstructionFunction !== 'function') {
      LOG.error('Unknown control instruction: ' + instructionName);
      return;
    }

    if (this.calledInstructions.has(instructionName)) {
      LOG.error(`Instruction ${instructionName} already called by ${this.logContextLabel(context)}.`);
      return;
    }

    this.calledInstructions.add(instructionName);

    context.instructionName = instructionName;
    controlInstructionFunction(context, ...instructionArgs);
  }
}
